import { useEffect, useState } from 'react';
import { deviceLabel } from '@icebreaker/shared/i18n';
import type { StaffSessionDto } from '@icebreaker/shared/staff';
import {
  Button,
  ConfirmDialog,
  EmptyState,
  ErrorBox,
  Modal,
  Skeleton,
  Table,
  Tag,
  tableStyles,
  useErrorMessage,
} from '@icebreaker/ui';
import { useTranslation } from '@/shared/i18n/i18n-provider';
import { useAppDispatch, useAppSelector } from '@/shared/store/hooks';
import type { RootState } from '@/shared/store/store';
import {
  fetchStaffSessions,
  revokeAllStaffSessions,
  revokeStaffSession,
  sessionsMutationReset,
} from '../store/staff-sessions.slice';
import styles from './staff-access.module.css';

type PendingAction =
  | { readonly kind: 'one'; readonly session: StaffSessionDto }
  | { readonly kind: 'all' };

/**
 * AÇIK OTURUMLAR KATMANI — personel YALNIZ KENDİ oturumlarını görür.
 *
 * Referans YOKTUR (devir paketinde panel hesap katmanı çizilmemiş); anatomi
 * panelin `UserDetailDialog` / `EventAttendeesDialog` desenidir: kicker +
 * başlık + açıklama + tablo + alt şerit. Kitin `Modal`ı, `Table`ı ve
 * `ConfirmDialog`u kullanılır; yeni bileşen yazılmaz.
 *
 * DÖRT HÂL: yükleniyor (iskelet) · boş · hata (tekrar dene) · dolu.
 *
 * İKİ AYAK (md. 1c): tekil sonlandırma o oturumu öldürür ve liste SUNUCUDAN
 * tazelenir (satır silinmez, yeniden okunur); "tümünü sonlandır" BU CİHAZI DA
 * kapsar ve personeli giriş ekranına götürür — arayüz "çıkıldı" derken sunucu
 * gerçekten çıkarmıştır. Bu cihazın satırında tekil eylem YOKTUR: kendi
 * oturumunu tek başına kapatmak "tümünü sonlandır"dan farksızdır ve iki yol
 * bırakmak birini test dışı bırakırdı.
 */
export function SessionsDialog({
  open,
  onClose,
}: {
  readonly open: boolean;
  readonly onClose: () => void;
}) {
  const t = useTranslation();
  const dispatch = useAppDispatch();
  const errorMessage = useErrorMessage();
  const sessions = useAppSelector((state: RootState) => state.staffSessions);
  const [pending, setPending] = useState<PendingAction | null>(null);

  useEffect(() => {
    if (!open) return;
    setPending(null);
    dispatch(sessionsMutationReset());
    dispatch(fetchStaffSessions.request());
  }, [open, dispatch]);

  // Tekil sonlandırma başarılı → onay penceresi kapanır (liste saga'da tazelendi).
  useEffect(() => {
    if (sessions.mutation.phase === 'succeeded' && pending) {
      setPending(null);
      dispatch(sessionsMutationReset());
    }
  }, [sessions.mutation.phase, pending, dispatch]);

  // Erken `return null` yok — odak sözleşmesi için (bkz. invite-staff-dialog).
  const loadingFirst = sessions.phase === 'loading' && sessions.items.length === 0;
  /*
   * `ConfirmDialog.error` ÖNCEDEN ÇEVRİLMİŞ cümle bekler (`ErrorText`ten farklı:
   * dikiş değil, çizim). Cümle burada bir kez kurulur; `error-message.guard`
   * KURAL 5 çift çeviri desenini metinden yakalar, ad açık olsun.
   */
  const mutationSentence = errorMessage(sessions.mutation.error) ?? undefined;
  const loadedEmpty = sessions.phase === 'succeeded' && sessions.items.length === 0;

  return (
    <Modal
      open={open}
      kicker={t.t('admin.panel.sessions.kicker')}
      title={t.t('admin.panel.sessions.title')}
      closeLabel={t.t('common.close')}
      wide
      onClose={onClose}
      footer={
        <div className={styles.sessionsFoot}>
          <span className={styles.note}>{t.t('admin.panel.sessions.body')}</span>
          <Button
            variant="dangerOutline"
            size="sm"
            disabled={sessions.phase !== 'succeeded' || sessions.items.length === 0}
            onClick={() => {
              dispatch(sessionsMutationReset());
              setPending({ kind: 'all' });
            }}
          >
            {t.t('admin.panel.sessions.revokeAll')}
          </Button>
        </div>
      }
    >
      <ErrorBox error={sessions.error} className={styles.errorBox}>
        <Button
          variant="secondary"
          size="sm"
          onClick={() => dispatch(fetchStaffSessions.request())}
        >
          {t.t('common.retry')}
        </Button>
      </ErrorBox>

      {loadingFirst ? (
        <Skeleton label={t.t('admin.panel.sessions.loading')} lines={3} lineHeight={20} />
      ) : loadedEmpty ? (
        <EmptyState
          title={t.t('admin.panel.sessions.emptyTitle')}
          body={t.t('admin.panel.sessions.emptyBody')}
        />
      ) : sessions.items.length > 0 ? (
        <Table>
          <thead>
            <tr>
              <th>{t.t('admin.panel.sessions.table.device')}</th>
              {/*
                ZAMAN TEK SÜTUN (açıldı + son kullanım alt alta). Katman 560 px
                çiziliyor — kitin `.modalWide` kuralı `width: 560px` ile kendi
                genişliğini eziyor (eski borç, raporda); dört sütun + eylem 512 px
                gövdeye sığmıyordu, son sütun kırpılıyordu (kanıt karesinde ölçüldü).
                Kit düzelince de üç sütun doğru kalır.
              */}
              <th>{t.t('admin.panel.sessions.table.time')}</th>
              <th aria-hidden="true" />
            </tr>
          </thead>
          <tbody>
            {sessions.items.map((session) => (
              <tr key={session.id}>
                {/*
                  Cihaz + "bu cihaz" rozeti TEK hücrede: ayrı bir Durum sütunu 820 px
                  katmanda beşinci sütunu kırpıyordu (ilk kanıt karesinde ölçüldü).
                */}
                <td className={styles.deviceCell}>
                  {/*
                    HAM `User-Agent` ÇİZİLMEZ (kod adı sızıntısı, 21.09.2026):
                    dize dahili paket adını taşıyabiliyor ve zaten okunur bir
                    cihaz adı değil. Eşleme web/mobil ile ORTAK
                    (`session-device-text.ts`); panel kendi "bilinmeyen"
                    metnini verir.
                  */}
                  <span className={styles.deviceText}>
                    {deviceLabel(t, session, {
                      unknownKey: 'admin.panel.sessions.unknownDevice',
                    })}
                  </span>
                  {session.isCurrent ? (
                    <Tag variant="accent">{t.t('admin.panel.sessions.current')}</Tag>
                  ) : null}
                </td>
                <td className={styles.timeCell}>
                  <span className={styles.timeRow}>
                    <span className={styles.timeLabel}>
                      {t.t('admin.panel.sessions.table.openedAt')}
                    </span>
                    {t.formatDate(session.createdAt, { dateStyle: 'medium', timeStyle: 'short' })}
                  </span>
                  <span className={styles.timeRow}>
                    <span className={styles.timeLabel}>
                      {t.t('admin.panel.sessions.table.lastUsedAt')}
                    </span>
                    {session.lastUsedAt
                      ? t.formatDate(session.lastUsedAt, {
                          dateStyle: 'medium',
                          timeStyle: 'short',
                        })
                      : t.t('admin.panel.sessions.neverUsed')}
                  </span>
                </td>
                <td className={tableStyles.cellActions}>
                  {session.isCurrent ? null : (
                    <Button
                      variant="dangerOutline"
                      size="sm"
                      onClick={() => {
                        dispatch(sessionsMutationReset());
                        setPending({ kind: 'one', session });
                      }}
                    >
                      {t.t('admin.panel.sessions.revoke')}
                    </Button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      ) : null}

      <ConfirmDialog
        open={pending !== null}
        tone="negative"
        title={t.t(
          pending?.kind === 'all'
            ? 'admin.panel.sessions.revokeAllTitle'
            : 'admin.panel.sessions.revokeTitle',
        )}
        body={t.t(
          pending?.kind === 'all'
            ? 'admin.panel.sessions.revokeAllBody'
            : 'admin.panel.sessions.revokeBody',
        )}
        confirmLabel={t.t(
          pending?.kind === 'all'
            ? 'admin.panel.sessions.revokeAll'
            : 'admin.panel.sessions.revoke',
        )}
        cancelLabel={t.t('common.cancel')}
        loading={sessions.mutation.phase === 'loading'}
        error={mutationSentence}
        onConfirm={() => {
          if (!pending) return;
          dispatch(
            pending.kind === 'all'
              ? revokeAllStaffSessions.request()
              : revokeStaffSession.request({ sessionId: pending.session.id }),
          );
        }}
        onCancel={() => {
          setPending(null);
          dispatch(sessionsMutationReset());
        }}
      />
    </Modal>
  );
}
